import { useCallback, useRef, useState } from 'react';

// Swipe-down-to-dismiss for bottom sheets. Returns:
//   handleProps — spread on the drag-handle element (the small pill at
//                 the top of the sheet). Restricting touch handlers to
//                 the handle keeps the inner scroll list usable —
//                 attaching to the whole sheet would intercept every
//                 vertical pan.
//   sheetStyle  — translateY/transition to spread on the sheet's
//                 `style` so the user sees it follow their finger.
//
// Threshold is the drag distance past which release dismisses. 80px is
// short enough to feel light but long enough to avoid accidental closes
// from a stray pan. Below threshold, the sheet snaps back.
export function useSwipeDismiss(onClose, { threshold = 80 } = {}) {
  const startY = useRef(null);
  const [dy, setDy] = useState(0);
  const [dragging, setDragging] = useState(false);

  const onTouchStart = useCallback((e) => {
    startY.current = e.touches[0].clientY;
    setDragging(true);
  }, []);

  const onTouchMove = useCallback((e) => {
    if (startY.current == null) return;
    const delta = e.touches[0].clientY - startY.current;
    // Only react to downward drag — upward pulls do nothing.
    setDy(delta > 0 ? delta : 0);
  }, []);

  const onTouchEnd = useCallback(() => {
    setDragging(false);
    if (dy > threshold) {
      onClose?.();
    }
    setDy(0);
    startY.current = null;
  }, [dy, threshold, onClose]);

  const handleProps = {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel: onTouchEnd,
    style: { touchAction: 'none' },
  };

  const sheetStyle = {
    transform: dy > 0 ? `translateY(${dy}px)` : undefined,
    transition: dragging ? 'none' : 'transform 200ms ease-out',
  };

  return { handleProps, sheetStyle };
}
